import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#A4DE6C', '#ffc658', '#998877', '#ff7300'];

function Individual({ data, page, totalPages, loading, loadMore }) {
  // Default to last 90 days
  const defaultStartDate = dayjs().subtract(90, 'day');
  const [dateRange, setDateRange] = useState({
    start: defaultStartDate,
    end: dayjs()
  });
  
  const [selectedManager, setSelectedManager] = useState('');
  const [selectedPartner, setSelectedPartner] = useState('');
  const [selectedActivityType, setSelectedActivityType] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [selectedMeetingTitle, setSelectedMeetingTitle] = useState('');

  // Get unique managers and partners
  const managers = useMemo(() => {
    const uniqueManagers = new Set(data.map(meeting => meeting['Exported By']));
    return Array.from(uniqueManagers).filter(Boolean).sort();
  }, [data]);

  const partners = useMemo(() => {
    const uniquePartners = new Set(data.map(meeting => meeting['Partner Name']));
    return Array.from(uniquePartners).filter(Boolean).sort();
  }, [data]);

  // Filter data based on selected filters
  const filteredData = useMemo(() => {
    // First deduplicate by Meeting ID, keeping only the most recent export
    const uniqueMeetings = new Map();
    data.forEach(meeting => {
      const id = meeting['Meeting ID'];
      const existingMeeting = uniqueMeetings.get(id);
      
      // If no existing meeting or this export is more recent, use this one
      if (!existingMeeting || new Date(meeting['Export Date']) > new Date(existingMeeting['Export Date'])) {
        uniqueMeetings.set(id, meeting);
      }
    });

    // Convert back to array and apply filters
    return Array.from(uniqueMeetings.values())
      .filter(meeting => {
        const meetingDate = dayjs(meeting['Meeting Date']);
        
        // Date range filter
        const isInDateRange = (meetingDate.isAfter(dateRange.start, 'day') && meetingDate.isBefore(dateRange.end, 'day')) || 
                           meetingDate.isSame(dateRange.start, 'day') ||
                           meetingDate.isSame(dateRange.end, 'day');

        // Manager filter
        const matchesManager = !selectedManager || 
                           meeting['Exported By'] === selectedManager;

        // Partner filter
        const matchesPartner = !selectedPartner || 
                           meeting['Partner Name'] === selectedPartner;

        return isInDateRange && matchesManager && matchesPartner;
      })
      .sort((a, b) => new Date(b['Meeting Date']) - new Date(a['Meeting Date']));
  }, [data, selectedManager, selectedPartner, dateRange]);

  // Partner meetings chart data
  const partnerChartData = useMemo(() => {
    // Log the input data for chart
    console.log('DEBUG: Creating partner chart data from filtered data:', {
      filteredDataLength: filteredData.length,
      uniqueMeetingIds: new Set(filteredData.map(m => m['Meeting ID'])).size
    });

    const partnerCounts = new Map();
    filteredData.forEach(meeting => {
      const partner = meeting['Partner Name'];
      partnerCounts.set(partner, (partnerCounts.get(partner) || 0) + 1);
    });

    return Array.from(partnerCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, value]) => ({ name: name.split(' ')[0], fullName: name, value }));
  }, [filteredData]);

  // Activity types chart data
  const activityChartData = useMemo(() => {
    const activityCounts = new Map();
    filteredData.forEach(meeting => {
      const activity = meeting['Meeting Type'];
      activityCounts.set(activity, (activityCounts.get(activity) || 0) + 1);
    });

    return Array.from(activityCounts.entries())
      .map(([name, value]) => ({ name, value }));
  }, [filteredData]);

  // External contacts with counts
  const externalContacts = useMemo(() => {
    const contactStats = new Map();
    
    filteredData.forEach(meeting => {
      const contacts = (meeting['External Attendees'] || '').split(',')
        .map(contact => contact.trim())
        .filter(Boolean)
        .filter(email => !email.endsWith('@codeium.com'));
      
      contacts.forEach(contact => {
        if (!contactStats.has(contact)) {
          contactStats.set(contact, { count: 0, meetings: [] });
        }
        const stats = contactStats.get(contact);
        stats.count++;
        stats.meetings.push(meeting);
      });
    });

    return Array.from(contactStats.entries())
      .map(([email, stats]) => ({
        email,
        count: stats.count,
        meetings: stats.meetings
      }))
      .sort((a, b) => b.count - a.count);
  }, [filteredData]);

  // Timeline data - meetings per week
  const timelineData = useMemo(() => {
    // Create an array of all weeks in the date range
    const weeks = [];
    let currentWeek = dateRange.start.startOf('week');
    const endWeek = dateRange.end.startOf('week');
    
    while (currentWeek.isBefore(endWeek) || currentWeek.isSame(endWeek, 'week')) {
      weeks.push(currentWeek.format('YYYY-MM-DD'));
      currentWeek = currentWeek.add(1, 'week');
    }

    // Count meetings for each week
    const weekCounts = new Map(weeks.map(week => [week, 0]));
    
    filteredData.forEach(meeting => {
      const weekStart = dayjs(meeting['Meeting Date']).startOf('week').format('YYYY-MM-DD');
      if (weekCounts.has(weekStart)) {
        weekCounts.set(weekStart, weekCounts.get(weekStart) + 1);
      }
    });

    // Convert to array and format for chart
    return Array.from(weekCounts.entries())
      .map(([week, count]) => ({
        week: dayjs(week).format('MM/DD'),
        count,
        fullDate: week // keep full date for sorting
      }))
      .sort((a, b) => dayjs(a.fullDate).diff(dayjs(b.fullDate)));
  }, [filteredData, dateRange]);

  // Helper function to get attendee count
  const getAttendeeCount = (meeting) => {
    const attendeeString = meeting['External Attendees'] || '';
    
    return attendeeString
      .split(',')
      .map(attendee => attendee.trim())
      .filter(Boolean)
      .filter(email => !email.endsWith('@codeium.com'))
      .length;
  };

  const handleAttendeesClick = (meeting) => {
    const attendeeString = meeting['External Attendees'] || '';
    
    const attendees = attendeeString
      .split(',')
      .map(attendee => attendee.trim())
      .filter(Boolean)
      .filter(email => !email.endsWith('@codeium.com'));
    
    setSelectedAttendees(attendees);
    setSelectedMeetingTitle(meeting['Meeting Title'] || meeting['Title']);
    setOpenDialog(true);
  };

  // Handle chart click events
  const handlePartnerClick = (data) => {
    setSelectedPartner(selectedPartner === data.fullName ? null : data.fullName);
  };

  const handleActivityClick = (data) => {
    setSelectedActivityType(selectedActivityType === data.name ? null : data.name);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1 }}>
          <Typography variant="body2">
            {payload[0].payload.fullName || payload[0].payload.name}: {payload[0].value} meetings
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  // Custom label for pie chart slices
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central">
        {value}
      </text>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Global Filters */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Partner Manager</InputLabel>
                <Select
                  value={selectedManager}
                  label="Partner Manager"
                  onChange={(e) => setSelectedManager(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {managers.map(manager => (
                    <MenuItem key={manager} value={manager}>{manager}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Partner</InputLabel>
                <Select
                  value={selectedPartner}
                  label="Partner"
                  onChange={(e) => setSelectedPartner(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {partners.map(partner => (
                    <MenuItem key={partner} value={partner}>{partner}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={dateRange.start}
                  onChange={(newValue) => setDateRange(prev => ({ ...prev, start: newValue }))}
                  sx={{ width: 200 }}
                />
                <DatePicker
                  label="End Date"
                  value={dateRange.end}
                  onChange={(newValue) => setDateRange(prev => ({ ...prev, end: newValue }))}
                  sx={{ width: 200 }}
                />
              </LocalizationProvider>
            </Stack>
          </Paper>
        </Grid>

        {/* Charts */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Top 10 Partners by Meetings
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={partnerChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  labelLine={false}
                  label={renderCustomizedLabel}
                  onClick={handlePartnerClick}
                >
                  {partnerChartData.map((entry, index) => (
                    <Cell 
                      key={entry.name}
                      fill={COLORS[index % COLORS.length]}
                      stroke={selectedPartner === entry.fullName ? '#000' : 'none'}
                      strokeWidth={selectedPartner === entry.fullName ? 2 : 0}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  layout="vertical" 
                  align="right" 
                  verticalAlign="middle"
                  wrapperStyle={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    fontSize: '12px',
                    paddingLeft: '10px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Activity Types
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={activityChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  labelLine={false}
                  label={renderCustomizedLabel}
                  onClick={handleActivityClick}
                >
                  {activityChartData.map((entry, index) => (
                    <Cell 
                      key={entry.name}
                      fill={COLORS[index % COLORS.length]}
                      stroke={selectedActivityType === entry.name ? '#000' : 'none'}
                      strokeWidth={selectedActivityType === entry.name ? 2 : 0}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  layout="vertical" 
                  align="right" 
                  verticalAlign="middle"
                  wrapperStyle={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    fontSize: '12px',
                    paddingLeft: '10px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Timeline Chart */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Meetings Timeline
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timelineData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week"
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  allowDecimals={false}
                />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <Paper sx={{ p: 1 }}>
                          <Typography variant="body2">
                            Week of {payload[0].payload.week}
                          </Typography>
                          <Typography variant="body2">
                            {payload[0].value} meetings
                          </Typography>
                        </Paper>
                      );
                    }
                    return null;
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#8884d8" 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Meetings"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Meetings Table and External Contacts */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Meetings
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Partner</TableCell>
                    <TableCell>Activity Type</TableCell>
                    <TableCell>External Attendees</TableCell>
                    <TableCell>Interlock Account</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.map((meeting, index) => (
                    <TableRow key={index}>
                      <TableCell>{dayjs(meeting['Meeting Date']).format('MM/DD/YYYY')}</TableCell>
                      <TableCell>{meeting['Meeting Title'] || meeting['Title']}</TableCell>
                      <TableCell>{meeting['Partner Name']}</TableCell>
                      <TableCell>{meeting['Meeting Type']}</TableCell>
                      <TableCell>
                        <Button
                          variant="text"
                          onClick={() => handleAttendeesClick(meeting)}
                          startIcon={<PersonIcon />}
                        >
                          {getAttendeeCount(meeting)} Attendees
                        </Button>
                      </TableCell>
                      <TableCell>{meeting['Interlock Account']}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress />
              </Box>
            )}
            {page < totalPages && !loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button variant="contained" onClick={loadMore}>
                  Load More
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              External Contacts
            </Typography>
            <List sx={{ maxHeight: 600, overflow: 'auto' }}>
              {externalContacts.map((contact) => (
                <ListItem
                  key={contact.email}
                  button
                  onClick={() => setSelectedContact(selectedContact === contact.email ? null : contact.email)}
                  selected={selectedContact === contact.email}
                >
                  <ListItemText
                    primary={contact.email}
                    secondary={`${contact.count} meetings`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* External Attendees Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6">
              External Attendees
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {selectedMeetingTitle}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <List>
              {selectedAttendees.map((attendee, index) => (
                <ListItem key={index}>
                  <ListItemText primary={attendee} />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Box>
  );
}

export default Individual;
